import {
	AfterViewInit,
	Component,
	computed,
	ElementRef,
	OnDestroy,
	signal,
	viewChild,
	ViewContainerRef
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslocoModule } from '@jsverse/transloco';
import assertNever from 'assert-never';
import { BarcodeDetector } from 'barcode-detector';

import { OrganizationsStoreService } from '@shure/cloud/shared/services/organizations-store-service';
import { SwitchOrganizationService } from '@shure/cloud/shared/switch-organization/feature-switch-organization';
import {
	PrismButtonComponent,
	PrismIconComponent,
	PrismLabelValueComponent,
	PrismProgressIndicatorComponent
} from '@shure/prism-angular-components';
import { ILogger } from '@shure/shared/angular/utils/logging';
import { asLabel } from '@shure/shared/models';

import { QrCodeClaimingService, SaveQrCodeResult } from '../../../../../services/qr-code-claiming.service';
import { parseClaimUrl } from '../../../../../utils/parse-claim-url';

interface StatusMessage {
	translocoKey: string;
	class: 'sh-error-message' | 'sh-info-message';
}

@Component({
	selector: 'sh-qr-code-scan-step',
	standalone: true,
	templateUrl: './qr-code-scan-step.component.html',
	styleUrl: './qr-code-scan-step.component.scss',
	imports: [
		PrismButtonComponent,
		PrismIconComponent,
		PrismLabelValueComponent,
		PrismProgressIndicatorComponent,
		TranslocoModule
	]
})
export class QrCodeScanStepComponent implements AfterViewInit, OnDestroy {
	private readonly logger: ILogger;
	private readonly scanPreviewRef = viewChild.required<ElementRef<HTMLVideoElement>>('scanPreview');
	private readonly barcodeDetector = new BarcodeDetector({ formats: ['qr_code'] });
	private mediaStream?: MediaStream;
	private decodingInterval?: NodeJS.Timeout;

	protected readonly scannedCodes = toSignal(this.qrCodeClaimingService.getScannedQrCodes$());
	protected readonly isSaving = signal(false);
	protected readonly hasCameraAccess = signal<boolean | undefined>(undefined);
	protected readonly statusMessage = signal<StatusMessage | undefined>(undefined);

	protected readonly organizationName = computed(() => {
		const orgName = this.organizationsStore.accountInformation()?.organizationDetails?.organizationName ?? '--';
		return orgName;
	});

	constructor(
		logger: ILogger,
		private readonly vcr: ViewContainerRef,
		private readonly qrCodeClaimingService: QrCodeClaimingService,
		private readonly switchOrganizationService: SwitchOrganizationService,
		private readonly organizationsStore: OrganizationsStoreService
	) {
		this.logger = logger.createScopedLogger('QrCodeScanStepComponent');
	}

	public ngAfterViewInit(): void {
		this.initializeQrCodeReader();
	}

	public ngOnDestroy(): void {
		clearInterval(this.decodingInterval);
		if (this.mediaStream) {
			for (const track of this.mediaStream.getTracks()) {
				track.stop();
			}
		}
	}

	protected onVideoLoaded(): void {
		// Detect barcode every 500ms until the user leaves this step
		clearInterval(this.decodingInterval);
		this.decodingInterval = setInterval(() => {
			this.detectBarcodes();
		}, 500);
	}

	protected async initializeQrCodeReader(): Promise<void> {
		try {
			this.mediaStream = await navigator.mediaDevices.getUserMedia({
				video: {
					facingMode: {
						ideal: 'environment'
					}
				}
			});
			this.getScanPreviewElement().srcObject = this.mediaStream;
			this.hasCameraAccess.set(true);
		} catch (e) {
			this.logger.error('initializeQrCodeReader', 'Unable to get user media', e);
			const error = <Error>e;
			if (error.name === 'NotAllowedError') {
				this.hasCameraAccess.set(false);
			} else if (error.name === 'NotFoundError') {
				this.statusMessage.set({
					translocoKey: asLabel('cloud.add-devices-dialog.claim-by-qr-code.camera-not-found-error'),
					class: 'sh-error-message'
				});
			} else {
				this.statusMessage.set({
					translocoKey: asLabel('cloud.add-devices-dialog.claim-by-qr-code.generic-camera-error'),
					class: 'sh-error-message'
				});
			}
		}
	}

	protected onChangeOrganizationClick(): void {
		this.qrCodeClaimingService.setGoToQrClaimingStepFlag();
		this.switchOrganizationService.openChangeOrgDialog(this.vcr);
	}

	private getScanPreviewElement(): HTMLVideoElement {
		const scanPreviewElement = this.scanPreviewRef().nativeElement;
		if (!scanPreviewElement) {
			throw new Error('Unable to get reference to scan preview element');
		}

		return scanPreviewElement;
	}

	private async detectBarcodes(): Promise<void> {
		// Don't run detection while we're currently saving a QR code to prevent duplicate scans
		if (this.isSaving()) {
			return;
		}

		const barcodes = await this.barcodeDetector.detect(this.getScanPreviewElement());
		if (barcodes.length === 0) {
			return;
		}

		// The QR code detection/parsing/saving is nearly instantaneous, so we're artificially leaving
		// the loading indicator for a short time as a visual indication that something happened.
		this.isSaving.set(true);
		setTimeout(() => {
			try {
				const scannedQrCode = parseClaimUrl(barcodes[0].rawValue);
				const result = this.qrCodeClaimingService.saveScannedQrCode(scannedQrCode);
				switch (result) {
					case SaveQrCodeResult.Success:
						this.statusMessage.set(undefined);
						break;
					case SaveQrCodeResult.Duplicate:
						this.statusMessage.set({
							translocoKey: asLabel('cloud.add-devices-dialog.claim-by-qr-code.code-already-scanned'),
							class: 'sh-info-message'
						});
						break;
					default:
						assertNever(result);
				}
			} catch (e) {
				const error = <Error>e;
				this.logger.error('detectBarcodes', error.message);
				this.statusMessage.set({
					translocoKey: asLabel('cloud.add-devices-dialog.claim-by-qr-code.invalid-serial-or-mac'),
					class: 'sh-error-message'
				});
			} finally {
				this.isSaving.set(false);
			}
		}, 1000);
	}
}
